import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Overlay from "../../../Overlay";
import Button from "../../../common/Button";
import { PAGE_STATE } from "../../../../constants";
import useUsers from "../../../../hooks/useUsers";
const assetsUri = import.meta.env.ASSETS_URI;

import { ReactComponent as UserSvg } from "../../../../assets/icons/user.svg";
import ComboBox from "../../../common/ComboBox";
import { Form, Formik } from "formik";
import TextBox from "../../../common/TextBox";
import FileUploader from "../../../common/FileUploader";
import { toast } from "react-toastify";
import { isValidNumber } from "../../../../helpers/common.helper";

const ViewCustomer = ({ editing: _editing, onClose, onSuccess }) => {
  const [editing, setEditing] = useState(_editing);
  const [pageState, setPageState] = useState(
    editing ? PAGE_STATE.LOADING : PAGE_STATE.IDLE
  );
  const [savingState, setSavingState] = useState(PAGE_STATE.IDLE);
  const [initialValues, setInitialValues] = useState({
    name: "",
    experience: 1,
    service: "",
    phone: "",
    streetAddress: "",
    apt: "",
    subDistrict: "",
    district: "",
    province: "",
    postalCode: "",
    type: "business",
  });

  const [staff, setStaff] = useState(undefined);
  const [avatar, setAvatar] = useState(undefined);
  const [newAvatarFile, setNewAvatarFile] = useState(undefined);

  const { getCompany, createCompany, patchCompany, uploadCompanyLogo } =
    useUsers();

  useEffect(() => {
    if (!editing) return;
    fetchData();
    return () => {};
  }, []);

  const fetchData = async () => {
    const { logo, name, experience, service, phone, address, enterprise } =
      await getCompany(editing);

    const { streetAddress, apt, subDistrict, district, province, postalCode } =
      address || {};

    setInitialValues({
      name,
      experience,
      service,
      phone,
      streetAddress,
      apt,
      subDistrict,
      district,
      province,
      postalCode,
      type: enterprise ? "enterprise" : "business",
    });

    setAvatar(`${assetsUri}/${logo}`);
    setPageState(PAGE_STATE.IDLE);
  };

  // * Handle avatar upload
  const handleFileChange = (files) => {
    if (files.length !== 1) return;

    setAvatar(URL.createObjectURL(files[0]));
    setNewAvatarFile(files[0]);
  };

  const handleValidate = async ({
    name,
    experience,
    service,
    phone,
    streetAddress,
    apt,
    subDistrict,
    district,
    province,
    postalCode,
    type,
  }) => {
    let errors = {};

    if (!name) errors.name = "Cannot be blank";
    if (!phone) errors.phone = "Cannot be blank";
    if (phone && !isValidNumber(phone))
      errors.phone = "Can only contain numbers";
    if (!streetAddress) errors.streetAddress = "Cannot be blank";
    if (!subDistrict) errors.subDistrict = "Cannot be blank";
    if (!district) errors.district = "Cannot be blank";
    if (!province) errors.province = "Cannot be blank";
    if (!postalCode) errors.postalCode = "Cannot be blank";
    if (postalCode && !Number(postalCode))
      errors.postalCode = "Can only contain numbers";

    if (!isValidNumber(experience))
      errors.experience = "Cannot only except numbers";

    return errors;
  };

  const handleSubmit = async (
    {
      name,
      experience,
      service,
      phone,
      streetAddress,
      apt,
      subDistrict,
      district,
      province,
      postalCode,
      type,
    },
    options
  ) => {
    setSavingState(PAGE_STATE.LOADING);

    // * Edit staff
    if (editing) {
      try {
        await patchCompany(editing, {
          name,
          experience: +experience,
          phone,
          enterprise: type === "enterprise",
          service: "temp",
          address: {
            streetAddress,
            apt,
            subDistrict,
            district,
            province,
            postalCode,
          },
          certificates: [],
          status: 1,
        });

        if (newAvatarFile) {
          await uploadCompanyLogo(editing, newAvatarFile);
        }

        setSavingState(PAGE_STATE.IDLE);
        onSuccess();
      } catch (error) {
        console.error(error.message);
        setSavingState(PAGE_STATE.ERROR);
      }
    }

    // * Create new staff
    if (!editing) {
      if (!avatar) {
        toast.warn(`Please select a profile picture`);
        setSavingState(PAGE_STATE.IDLE);
        return;
      }

      try {
        const company = await createCompany({
          name,
          experience: +experience,
          phone,
          enterprise: type === "enterprise",
          service: "temp",
          address: {
            streetAddress,
            apt,
            subDistrict,
            district,
            province,
            postalCode,
          },
          certificates: [],
          status: 1,
        });

        if (newAvatarFile) {
          await uploadCompanyLogo(company._id, newAvatarFile);
        }

        setSavingState(PAGE_STATE.IDLE);
        onSuccess();
      } catch (error) {
        console.error(error.message);
        setSavingState(PAGE_STATE.ERROR);
      }
    }
  };

  if (pageState === PAGE_STATE.LOADING) {
    return <Overlay />;
  }

  return (
    <Overlay
      style={{
        justifyContent: "flex-start",
      }}
      onClose={onClose}
      content={
        <div className="admin-staffs-view">
          <Formik
            initialValues={initialValues}
            validate={handleValidate}
            onSubmit={handleSubmit}
          >
            <Form autoComplete="off">
              <h2 className="admin-staffs-view__header">Company Information</h2>
              <div className="admin-staffs-view-info">
                <div className="admin-staffs-view-info-avatar">
                  {!avatar && (
                    <UserSvg className="admin-staffs-view-info-avatar__empty-state" />
                  )}
                  {avatar && (
                    <img
                      className="admin-staffs-view-info-avatar__image"
                      src={avatar}
                      alt="avatar"
                    />
                  )}
                </div>
                <FileUploader
                  id="avatar"
                  label={"Upload picture"}
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <h3 className="admin-staffs-view__heading3">Information</h3>
                <TextBox label="Name" name="name" />
                <div className="admin-staffs-view-info-split">
                  <TextBox
                    label="Years of experience"
                    name="experience"
                    type="number"
                  />
                  <ComboBox
                    label="Type"
                    name="type"
                    options={[
                      { value: "business", name: "Business" },
                      { value: "enterprise", name: "Enterprise" },
                    ]}
                  />
                </div>
                <TextBox label="Phone no" name="phone" type="number" />
                <h3 className="admin-staffs-view__heading3">Address</h3>
                <TextBox label="Street address" name="streetAddress" />
                <TextBox label="Apartment, etc" name="apt" />
                <div className="admin-staffs-view-info-split">
                  <TextBox label="Sub District" name="subDistrict" />
                  <TextBox label="District" name="district" />
                  <TextBox label="Province" name="province" />
                  <TextBox label="Postal Code" name="postalCode" />
                </div>
              </div>
              <div className="admin-staffs-view-footer">
                {savingState === PAGE_STATE.LOADING && <>Saving...</>}
                {savingState === PAGE_STATE.ERROR && <>Error saving</>}
                {savingState === PAGE_STATE.IDLE && (
                  <Button label={"Save"} type="submit" />
                )}
              </div>
            </Form>
          </Formik>
        </div>
      }
    />
  );
};

ViewCustomer.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  editing: PropTypes.string,
};

ViewCustomer.defaultProps = {
  onClose: () => {},
  onSuccess: () => {},
  editing: undefined,
};

export default ViewCustomer;
